import React, { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { useNavigate } from "react-router-dom";
import useScrollToTop from "../../../hooks/useScollToTop";
import {
  getClubByUser,
  createClub,
  updateClub,
  deleteClub,
  getClubById,
} from "../../../services/clubServices";
import ClubModal from "./ClubModal";
import { isDemoUser } from "./../../../utils/isDemoUser";
import {
  FaArrowRight,
  FaBuilding,
  FaEdit,
  FaMapMarkerAlt,
  FaPlus,
  FaTrash,
  FaUniversity,
  FaUserCircle,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const VotreClub = () => {
  useScrollToTop();
  const { addNotification } = useNotification();
  const navigate = useNavigate();

  let userInfo = null;
  try {
    userInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
  } catch (error) {
    userInfo = null;
  }

  const userId = userInfo?.userId;
  const demoMode = isDemoUser(userInfo);

  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchClub();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const fetchClub = async () => {
    try {
      setLoading(true);

      const data = demoMode
        ? await getClubById(2)
        : await getClubByUser(userId);

      if (data && data.id) {
        setClub(data);
      } else {
        setClub(null);
      }
    } catch (error) {
      console.error("Erreur lors du chargement du club :", error);
      setClub(null);
      addNotification("Erreur lors du chargement du club.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdateClub = async (clubData) => {
    try {
      setSubmitting(true);

      if (demoMode) {
        addNotification("Action désactivée en mode Démo.", "warning");
        return;
      }

      if (editMode && club?.id) {
        await updateClub(club.id, clubData);
        addNotification("Club mis à jour avec succès.", "success");
      } else {
        await createClub({ ...clubData, user_id: userId });
        addNotification("Club créé avec succès.", "success");
      }

      await fetchClub();
      setModalOpen(false);
    } catch (error) {
      console.error("Erreur lors de la création / mise à jour du club :", error);
      addNotification("Erreur lors de la mise à jour du club.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClub = async () => {
    if (!club?.id) return;

    if (demoMode) {
      addNotification("Action désactivée en mode Démo.", "warning");
      return;
    }

    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce club ?")) return;

    try {
      setSubmitting(true);
      await deleteClub(club.id);
      setClub(null);
      addNotification("Club supprimé avec succès.", "success");
    } catch (error) {
      console.error("Erreur lors de la suppression du club :", error);
      addNotification("Erreur lors de la suppression du club.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const headerBadge = useMemo(() => {
    if (demoMode) return "Mode Démo";
    return "Gestion du club";
  }, [demoMode]);

  return (
    <div className="w-full bg-slate-50 pb-12 pt-[100px] dark:bg-slate-950">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6">
        {/* Header */}
        <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6 lg:p-8 mt-4">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                Votre compte
              </p>
              <h1 className="mt-1 text-3xl font-black text-slate-900 dark:text-slate-100">
                Votre club
              </h1>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                Créez ou modifiez les informations principales de votre club.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {headerBadge}
              </span>

              {!loading && userId && !club && (
                <button
                  onClick={() => {
                    setEditMode(false);
                    setModalOpen(true);
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  <FaPlus />
                  Créer un club
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Contenu */}
        <section className="mt-5">
          {loading ? (
            <div className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
              <div className="animate-pulse rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mx-auto h-32 w-32 rounded-full bg-slate-200 dark:bg-slate-800" />
                <div className="mx-auto mt-5 h-6 w-40 rounded bg-slate-200 dark:bg-slate-800" />
                <div className="mx-auto mt-3 h-4 w-28 rounded bg-slate-200 dark:bg-slate-800" />
              </div>

              <div className="animate-pulse rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="h-6 w-48 rounded bg-slate-200 dark:bg-slate-800" />
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800" />
                  <div className="h-28 rounded-2xl bg-slate-200 dark:bg-slate-800 sm:col-span-2" />
                </div>
              </div>
            </div>
          ) : !userId ? (
            <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                <FaUserCircle className="text-4xl" />
              </div>

              <h2 className="mt-5 text-2xl font-black text-slate-900 dark:text-slate-100">
                Connexion requise
              </h2>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                Impossible de récupérer votre compte. Veuillez vous reconnecter.
              </p>

              <button
                onClick={() => navigate("/login")}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                Se reconnecter
                <FaArrowRight />
              </button>
            </div>
          ) : club ? (
            <div className="grid gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
              {/* Carte visuelle */}
              <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-6 text-white">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
                    Fiche club
                  </p>
                  <h2 className="mt-2 text-2xl font-black">{club.nom_club}</h2>
                  <p className="mt-2 text-sm text-white/80">
                    {club.departement_club || "Département non renseigné"}
                  </p>
                </div>

                <div className="p-6">
                  <div className="flex flex-col items-center text-center">
                    {club.logo_url ? (
                      <img
                        src={club.logo_url}
                        alt={club.nom_club}
                        className="h-32 w-32 rounded-full border-4 border-white object-cover shadow-xl ring-1 ring-slate-200 dark:border-slate-900 dark:ring-slate-700"
                      />
                    ) : (
                      <div className="flex h-32 w-32 items-center justify-center rounded-full bg-slate-100 text-slate-500 shadow-xl dark:bg-slate-800 dark:text-slate-300">
                        <FaBuilding className="text-5xl" />
                      </div>
                    )}

                    <div className="mt-5 inline-flex rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      {demoMode ? "Consultation Démo" : "Club actif"}
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col gap-3">
                    <button
                      onClick={() => {
                        setEditMode(true);
                        setModalOpen(true);
                      }}
                      className={cn(
                        "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 font-semibold text-white transition",
                        demoMode
                          ? "bg-slate-400 dark:bg-slate-700"
                          : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                      )}
                    >
                      <FaEdit />
                      {demoMode ? "Voir / tester la fiche" : "Modifier"}
                    </button>

                    <button
                      onClick={handleDeleteClub}
                      disabled={demoMode || submitting}
                      className={cn(
                        "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 font-semibold text-white transition",
                        demoMode || submitting
                          ? "cursor-not-allowed bg-slate-400 dark:bg-slate-700"
                          : "bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                      )}
                    >
                      <FaTrash />
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>

              {/* Détails */}
              <div className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                      Informations
                    </p>
                    <h3 className="text-2xl font-black text-slate-900 dark:text-slate-100">
                      Identité du club
                    </h3>
                  </div>

                  {demoMode && (
                    <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                      Modifications désactivées en démo
                    </span>
                  )}
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                      <FaUniversity />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Nom du club
                    </p>
                    <p className="mt-2 text-lg font-bold text-slate-900 dark:text-slate-100">
                      {club.nom_club || "Non renseigné"}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                      <FaMapMarkerAlt />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Département
                    </p>
                    <p className="mt-2 text-lg font-bold text-slate-900 dark:text-slate-100">
                      {club.departement_club || "Non renseigné"}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60 sm:col-span-2">
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                      <FaBuilding />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                      Adresse
                    </p>
                    <p className="mt-2 text-lg font-bold text-slate-900 dark:text-slate-100">
                      {club.adresse_club || "Non renseignée"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="mx-auto max-w-2xl text-center">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">
                  <FaBuilding className="text-4xl" />
                </div>

                <h2 className="mt-5 text-3xl font-black text-slate-900 dark:text-slate-100">
                  Aucun club créé
                </h2>
                <p className="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-400">
                  Ajoutez les informations essentielles de votre club pour
                  commencer à personnaliser votre espace.
                </p>

                <button
                  onClick={() => {
                    setEditMode(false);
                    setModalOpen(true);
                  }}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  <FaPlus />
                  Créer mon club
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {modalOpen && (
        <ClubModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleCreateOrUpdateClub}
          club={editMode ? club : null}
          demoMode={demoMode}
          isSubmitting={submitting}
        />
      )}
    </div>
  );
};

export default VotreClub;